import React, { useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Heading,
  VStack,
  HStack,
  Badge,
  Container,
  useColorModeValue,
  Icon,
  Avatar,
  AvatarGroup,
  Button,
  useToast,
  Spinner,
  ButtonGroup,
  Select,
} from "@chakra-ui/react";
import { FiUsers, FiActivity, FiAlertTriangle, FiList, FiRefreshCw } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import ApexChart from "react-apexcharts";
import {
  getAllPersons,
  authenticateVisaVita,
  isAuthenticated as isVisaVitaAuthenticated,
  getPatientDemographics,
  getSubgroups,
} from "api/visaVitaService";
import CohortAnalysis from "./CohortAnalysis";
import { PATIENT_GROUPS, groupLabel } from "./PatientManagement";

const BiomDashboard = () => {
  // Color mode values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const statBg = useColorModeValue("gray.50", "gray.700");
  const purpleGradient = useColorModeValue(
    "linear(to-r, purple.400, purple.600)",
    "linear(to-r, purple.300, purple.500)"
  );

  const history = useHistory();
  const toast = useToast();

  // State for real patient data
  const [recentPatients, setRecentPatients] = useState([]);
  const [alertsData, setAlertsData] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [authenticationStatus, setAuthenticationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [kpiData, setKpiData] = useState({
    totalPatients: 0,
    activePatients: 0,
    criticalAlerts: 0,
    avgHeartRate: 78,
    totalMeasurements: 0,
    systemUptime: 99.8
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [patientGrowthData, setPatientGrowthData] = useState({ series: [], categories: [] });
  const [publicTokenData, setPublicTokenData] = useState({ series: [], labels: [] });
  const [ageDistributionData, setAgeDistributionData] = useState({
    stress: { series: [], categories: [] },
    cursano: { series: [], categories: [] },
    glucose: { series: [], categories: [] }
  });
  const [allPersons, setAllPersons] = useState([]);
  const [demographicPatients, setDemographicPatients] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  // 'all' or a group token; analyses below are limited to it
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedSubgroupId, setSelectedSubgroupId] = useState('');
  const [cohortRefreshKey, setCohortRefreshKey] = useState(0);

  // Function to process real demographics data
  const processDemographicsData = (patients) => {
    if (!patients || patients.length === 0) {
      return {
        glucose: { series: [], categories: [] }
      };
    }

    const ageGroups = ['0-2', '3-6', '7-10', '11-13', '14-17', '18-25', '26-35', '36-55', '56+'];

    const demographicsByType = {
      glucose: { female: new Array(ageGroups.length).fill(0), male: new Array(ageGroups.length).fill(0) }
    };

    patients.forEach(patient => {
      const { ageGroup, sex, externalIdentifier } = patient;

      const patientType = 'glucose';

      // Find the age group index
      const ageGroupIndex = ageGroups.indexOf(ageGroup);
      if (ageGroupIndex !== -1 && sex) {
        const gender = sex.toUpperCase() === 'F' ? 'female' : 'male';
        demographicsByType[patientType][gender][ageGroupIndex]++;
      } else {
        console.warn(`Invalid patient data: ${externalIdentifier}, ageGroup: ${ageGroup}, sex: ${sex}`);
      }
    });

    // Convert to chart format
    return {
      glucose: {
        series: [
          { name: 'Female (F)', data: demographicsByType.glucose.female },
          { name: 'Male (M)', data: demographicsByType.glucose.male }
        ],
        categories: ageGroups
      }
    };
  };

  // Function to process publicToken distribution
  const processPublicTokenData = (patients) => {
    if (!patients || patients.length === 0) {
      return { series: [], labels: [] };
    }

    // Count publicToken types
    const tokenCounts = {
      'VISAVITA_GLUCOSE': 0,
      'VISAVITA_STRESS': 0,
      'VISAVITA_CORSANO': 0
    };

    patients.forEach(patient => {
      const publicToken = patient.publicToken || 'UNKNOWN';
      if (tokenCounts.hasOwnProperty(publicToken)) {
        tokenCounts[publicToken]++;
      }
    });

    // Filter out zero counts and prepare data
    const filteredData = Object.entries(tokenCounts)
      .filter(([_, count]) => count > 0)
      .map(([token, count]) => ({ token, count }));

    const series = filteredData.map(item => item.count);
    const labels = filteredData.map(item => {
      switch (item.token) {
        case 'VISAVITA_GLUCOSE': return 'Glucose';
        case 'VISAVITA_STRESS': return 'Stress';
        case 'VISAVITA_CORSANO': return 'Cursano';
        default: return item.token;
      }
    });

    return { series, labels };
  };

  // Function to process patient growth data
  const processPatientGrowthData = (patients) => {
    if (!patients || patients.length === 0) {
      return { series: [], categories: [] };
    }

    const patientsWithDates = patients.map(patient => {
      const dateField = patient.createdAt ||
        patient.created_at ||
        patient.dateCreated ||
        patient.date_created ||
        patient.registrationDate ||
        patient.mapping?.createdAt ||
        patient.mapping?.created_at ||
        new Date().toISOString();

      return {
        ...patient,
        creationDate: new Date(dateField)
      };
    }).filter(patient => !isNaN(patient.creationDate.getTime()));

    patientsWithDates.sort((a, b) => a.creationDate - b.creationDate);

    const monthlyGrowth = {};
    const cumulativeGrowth = {};
    let cumulativeCount = 0;

    const patients2025 = patientsWithDates
      .filter(patient => patient.creationDate.getFullYear() === 2025)
      .sort((a, b) => a.creationDate - b.creationDate);

    patients2025.forEach(patient => {
      const monthKey = patient.creationDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });

      if (!monthlyGrowth[monthKey]) {
        monthlyGrowth[monthKey] = 0;
      }
      monthlyGrowth[monthKey]++;

      cumulativeCount++;
      cumulativeGrowth[monthKey] = cumulativeCount;
    });

    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2025, i, 1);
      months.push(date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }));
    }

    const newRegistrations = months.map(month => monthlyGrowth[month] || 0);
    const totalPatients = months.map(month => cumulativeGrowth[month] || 0);

    let lastCumulative = 0;
    for (let i = 0; i < totalPatients.length; i++) {
      if (totalPatients[i] === 0 && lastCumulative > 0) {
        totalPatients[i] = lastCumulative;
      } else if (totalPatients[i] > 0) {
        lastCumulative = totalPatients[i];
      }
    }

    return {
      series: [
        {
          name: 'Total Patients',
          data: totalPatients
        },
        {
          name: 'New Registrations',
          data: newRegistrations
        }
      ],
      categories: months
    };
  };

  // Patients in a subgroup, per subgroup of the selected group
  const processSubgroupData = (patients) => {
    const counts = new Map();
    patients.forEach(patient => {
      const name = patient.subgroup?.name || 'No subgroup';
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const entries = [...counts.entries()].sort(([a], [b]) => (a === 'No subgroup') - (b === 'No subgroup'));
    return { series: entries.map(([, count]) => count), labels: entries.map(([name]) => name) };
  };

  useEffect(() => {
    loadPatientsData();
  }, []);

  // Everything on the page except the cohort analysis follows the selected group and subgroup
  useEffect(() => {
    const scopedPersons = allPersons.filter(person => {
      if (selectedGroup === 'all') return true;
      if (person.publicToken !== selectedGroup) return false;
      return !selectedSubgroupId || String(person.subgroup?.id) === String(selectedSubgroupId);
    });

    setKpiData(current => ({
      ...current,
      totalPatients: scopedPersons.length,
      activePatients: scopedPersons.length,
    }));

    setRecentPatients(scopedPersons.slice(0, 5).map((person, index) => ({
      id: person.mapping?.id || index,
      identifier: person.mapping?.externalIdentifier || `Unknown-${index + 1}`,
      externalIdentifier: person.mapping?.externalIdentifier || 'N/A',
      publicToken: person.publicToken || 'N/A',
      name: `Patient ${person.mapping?.externalIdentifier || index + 1}`,
      email: person.email || 'N/A',
      age: 'N/A',
      gender: person.gender || 'N/A',
      status: 'stable',
      condition: 'General',
      measurements: 0,
      avatar: `P${index + 1}`
    })));

    setPatientGrowthData(processPatientGrowthData(scopedPersons));
    setPublicTokenData(selectedGroup === 'all' ? processPublicTokenData(scopedPersons) : processSubgroupData(scopedPersons));

    const scopedIdentifiers = new Set(scopedPersons.map(person => person.mapping?.externalIdentifier));
    setAgeDistributionData(processDemographicsData(
      demographicPatients.filter(patient => scopedIdentifiers.has(patient.externalIdentifier))
    ));
  }, [allPersons, demographicPatients, selectedGroup, selectedSubgroupId]);

  const checkAuthenticationStatus = () => {
    const accessToken = localStorage.getItem("accessToken");
    const user = localStorage.getItem("user");
    const isMainAppAuthenticated = !!(accessToken && user);

    setAuthenticationStatus(isMainAppAuthenticated);
    return isMainAppAuthenticated;
  };

  const ensureVisaVitaAuthentication = async () => {
    if (isVisaVitaAuthenticated()) {
      return true;
    }

    try {
      const username = localStorage.getItem("visaVitaUsername");
      const password = localStorage.getItem("visaVitaPassword");

      if (!username || !password) {
        throw new Error("No VisaVita credentials found");
      }

      await authenticateVisaVita(username, password);
      return true;
    } catch (error) {
      console.error('Auth failed:', error.message);
      throw error;
    }
  };

  // Fetch all available persons
  const fetchAllPersons = async () => {
    try {
      const result = await getAllPersons();

      if (result?.success && result.persons) {
        return result.persons;
      } else {
        console.warn('No persons data received');
        return [];
      }
    } catch (error) {
      console.error('API error:', error.message);
      throw error;
    }
  };

  const loadPatientsData = async () => {
    setPatientsLoading(true);
    setError(null);

    try {
      if (!checkAuthenticationStatus()) {
        throw new Error("Please sign in first");
      }
      const visaVitaAuth = await ensureVisaVitaAuthentication();
      if (!visaVitaAuth) {
        throw new Error("VisaVita auth failed");
      }
      const persons = await fetchAllPersons();
      if (!persons || persons.length === 0) {
        throw new Error("No persons found");
      }

      // KPIs, charts and recent patients are derived from these for the selected group (see the effect below)
      setAllPersons(persons);
      setAlertsData([]);
      setLastUpdated(new Date());
      setCohortRefreshKey(key => key + 1);

      try {
        setSubgroups(await getSubgroups());
      } catch (subgroupError) {
        console.error('Error fetching subgroups:', subgroupError.message);
        setSubgroups([]);
      }

      // Fetch demographics data for age distribution
      setLoadingDemographics(true);
      try {
        const demographicsResult = await getPatientDemographics();

        if (demographicsResult?.success && demographicsResult.patients) {
          setDemographicPatients(demographicsResult.patients);
        } else {
          console.warn('No demographics data received, using empty data');
          setDemographicPatients([]);
        }
      } catch (demographicsError) {
        console.error('Error fetching demographics:', demographicsError.message);
        setDemographicPatients([]);
      } finally {
        setLoadingDemographics(false);
      }

    } catch (error) {
      setError(`Error: ${error.message}`);

      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });

      // Reset to zero
      setKpiData({
        totalPatients: 0,
        activePatients: 0,
        criticalAlerts: 0,
        avgHeartRate: 0,
        totalMeasurements: 0,
        systemUptime: 99.8
      });
      setAllPersons([]);
      setDemographicPatients([]);
      setRecentPatients([]);
      setAlertsData([]);
      setPatientGrowthData({ series: [], categories: [] });
      setPublicTokenData({ series: [], labels: [] });
      setAgeDistributionData({
        glucose: { series: [], categories: [] }
      });
    } finally {
      setPatientsLoading(false);
      setLoadingPersons(false);
      setLoadingDemographics(false);
    }
  };

  const patientOverviewChart = {
    series: patientGrowthData.series.length > 0 ? patientGrowthData.series : [
      { name: 'Total Patients', data: [] },
      { name: 'New Registrations', data: [] }
    ],
    options: {
      chart: {
        type: 'area',
        height: 300,
        background: 'transparent',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true
          }
        }
      },
      colors: ['#3182CE', '#38A169'],
      stroke: {
        curve: 'smooth',
        width: [3, 2],
        dashArray: [0, 5]
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          opacityFrom: 0.4,
          opacityTo: 0.1,
        }
      },
      xaxis: {
        categories: patientGrowthData.categories.length > 0 ? patientGrowthData.categories :
          ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: {
          style: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
          rotate: -45
        },
        title: {
          text: 'Time Period',
          style: { color: useColorModeValue('#2D3748', '#A0AEC0') }
        }
      },
      yaxis: [
        {
          seriesName: 'Total Patients',
          title: {
            text: 'Total Patients',
            style: { color: '#3182CE' }
          },
          labels: {
            style: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
            formatter: function (val) {
              return Math.round(val);
            }
          }
        },
        {
          seriesName: 'New Registrations',
          opposite: true,
          title: {
            text: 'New Registrations',
            style: { color: '#38A169' }
          },
          labels: {
            style: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
            formatter: function (val) {
              return Math.round(val);
            }
          }
        }
      ],
      legend: {
        labels: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
        position: 'top',
        horizontalAlign: 'center'
      },
      grid: {
        borderColor: useColorModeValue('#E2E8F0', '#4A5568')
      },
      theme: {
        mode: useColorModeValue('light', 'dark')
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (val, opts) {
            if (opts.seriesIndex === 0) {
              return val + " total patients";
            } else {
              return val + " new registrations";
            }
          }
        }
      },
      annotations: {
        xaxis: patientGrowthData.series.length > 0 && patientGrowthData.series[0].data.some(val => val > 0) ? [{
          x: patientGrowthData.categories[patientGrowthData.categories.length - 1],
          borderColor: '#3182CE',
          label: {
            borderColor: '#3182CE',
            style: {
              color: '#fff',
              background: '#3182CE',
            },
            text: 'Latest Data'
          }
        }] : []
      }
    }
  };

  const publicTokenDistribution = {
    series: publicTokenData.series.length > 0 ? publicTokenData.series : [0],
    options: {
      chart: {
        type: 'donut',
        background: 'transparent'
      },
      colors: selectedGroup === 'all'
        ? ['#38A169', '#E53E3E', '#3182CE']
        : ['#805AD5', '#3182CE', '#38A169', '#DD6B20', '#D69E2E', '#319795', '#E53E3E', '#A0AEC0'],
      labels: publicTokenData.labels.length > 0 ? publicTokenData.labels : ['No Data'],
      legend: {
        position: 'bottom',
        labels: { colors: useColorModeValue('#2D3748', '#A0AEC0') }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                color: useColorModeValue('#2D3748', '#A0AEC0')
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 'bold',
                color: useColorModeValue('#2D3748', '#A0AEC0')
              },
              total: {
                show: true,
                label: 'Total',
                fontSize: '16px',
                color: useColorModeValue('#2D3748', '#A0AEC0')
              }
            }
          }
        }
      },
      theme: {
        mode: useColorModeValue('light', 'dark')
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " patients";
          }
        }
      }
    }
  };


  // Function to create age distribution chart configuration
  const createAgeDistributionChart = (data, title, color) => {
    return {
      series: data.series.length > 0 ? data.series : [
        { name: 'Female (F)', data: [] },
        { name: 'Male (M)', data: [] }
      ],
      options: {
        chart: {
          type: 'bar',
          height: 350,
          background: 'transparent',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true
            }
          }
        },
        colors: ['#E91E63', '#4682B4'], // Magenta for Female, Steel Blue for Male
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: data.categories.length > 0 ? data.categories :
            ['0-2', '3-6', '7-10', '11-13', '14-17', '18-25', '26-35', '36-55', '56+'],
          title: {
            text: 'Age Group',
            style: { color: useColorModeValue('#2D3748', '#A0AEC0') }
          },
          labels: {
            style: { colors: useColorModeValue('#2D3748', '#A0AEC0') }
          }
        },
        yaxis: {
          title: {
            text: 'Number of Subjects',
            style: { color: useColorModeValue('#2D3748', '#A0AEC0') }
          },
          labels: {
            style: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
            formatter: function (val) {
              return Math.round(val);
            }
          }
        },
        legend: {
          labels: { colors: useColorModeValue('#2D3748', '#A0AEC0') },
          position: 'top',
          horizontalAlign: 'center'
        },
        grid: {
          borderColor: useColorModeValue('#E2E8F0', '#4A5568')
        },
        theme: {
          mode: useColorModeValue('light', 'dark')
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " subjects";
            }
          }
        },
        title: {
          text: title,
          align: 'center',
          style: {
            color: useColorModeValue('#2D3748', '#A0AEC0'),
            fontSize: '16px',
            fontWeight: 'bold'
          }
        }
      }
    };
  };

  // Function to create sex distribution pie chart configuration
  const createSexDistributionPieChart = (data, title, color) => {
    const femaleTotal = data.series.length > 0 && data.series[0].data.length > 0
      ? data.series[0].data.reduce((sum, val) => sum + val, 0) : 0;
    const maleTotal = data.series.length > 0 && data.series[1].data.length > 0
      ? data.series[1].data.reduce((sum, val) => sum + val, 0) : 0;

    const total = femaleTotal + maleTotal;
    const femalePercentage = total > 0 ? ((femaleTotal / total) * 100).toFixed(1) : 0;
    const malePercentage = total > 0 ? ((maleTotal / total) * 100).toFixed(1) : 0;

    return {
      series: [femaleTotal, maleTotal],
      options: {
        chart: {
          type: 'pie',
          height: 300,
          background: 'transparent'
        },
        colors: ['#E91E63', '#4682B4'],
        labels: [`Female (F): ${femaleTotal} (${femalePercentage}%)`, `Male (M): ${maleTotal} (${malePercentage}%)`],
        legend: {
          position: 'bottom',
          labels: { colors: useColorModeValue('#2D3748', '#A0AEC0') }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  color: useColorModeValue('#2D3748', '#A0AEC0')
                },
                value: {
                  show: true,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: useColorModeValue('#2D3748', '#A0AEC0')
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px',
                  color: useColorModeValue('#2D3748', '#A0AEC0'),
                  formatter: function () {
                    return total.toString();
                  }
                }
              }
            }
          }
        },
        theme: {
          mode: useColorModeValue('light', 'dark')
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " subjects";
            }
          }
        },
        title: {
          text: title,
          align: 'center',
          style: {
            color: useColorModeValue('#2D3748', '#A0AEC0'),
            fontSize: '16px',
            fontWeight: 'bold'
          }
        }
      }
    };
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'Critical': return 'red';
      case 'Warning': return 'orange';
      case 'Info': return 'blue';
      default: return 'gray';
    }
  };

  return (
    <Container maxW="full" py={8} bg={bgColor} minH="100vh">
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box>
          <HStack justify="space-between" align="center" mb={6} mt={6}>
            <VStack align="start" spacing={1}>
              <Heading
                size="xl"
                bgGradient={purpleGradient}
                bgClip="text"
              >
                Visa Vita Analytics Dashboard
              </Heading>
              <Text color="gray.600" fontSize="lg">
                Real-time patient monitoring and health insights
              </Text>
              {lastUpdated && (
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </Text>
                  <Badge size="xs" colorScheme="green" variant="subtle">
                    LIVE DATA
                  </Badge>
                </HStack>
              )}
            </VStack>

            <HStack spacing={3}>
              <Button
                leftIcon={<Icon as={FiRefreshCw} />}
                colorScheme="teal"
                variant="outline"
                onClick={loadPatientsData}
                isLoading={patientsLoading}
                size="md"
              >
                Refresh
              </Button>
              <Button
                leftIcon={<Icon as={FiList} />}
                colorScheme="purple"
                variant="outline"
                onClick={() => history.push("/admin/patients-list")}
                size="md"
              >
                Patients List
              </Button>
            </HStack>
          </HStack>
        </Box>

        {/* Group selection: all groups combined, or one group and optionally one of its subgroups */}
        <Box bg={cardBg} shadow="md" borderRadius="lg" p={5}>
          <HStack justify="space-between" wrap="wrap" spacing={4} rowGap={3}>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" color="purple.600">Patient group</Text>
              <Text fontSize="sm" color="gray.500">
                {selectedGroup === 'all'
                  ? 'All groups combined. Choose a group for its cohort analysis.'
                  : `Analyses below cover ${groupLabel(selectedGroup)} patients only.`}
              </Text>
            </VStack>
            <HStack spacing={3} wrap="wrap" rowGap={2}>
              <ButtonGroup size="sm" isAttached variant="outline" colorScheme="purple">
                <Button
                  variant={selectedGroup === 'all' ? 'solid' : 'outline'}
                  onClick={() => { setSelectedGroup('all'); setSelectedSubgroupId(''); }}
                >
                  All groups
                </Button>
                {PATIENT_GROUPS.map(group => (
                  <Button
                    key={group.token}
                    variant={selectedGroup === group.token ? 'solid' : 'outline'}
                    onClick={() => { setSelectedGroup(group.token); setSelectedSubgroupId(''); }}
                  >
                    {group.label}
                  </Button>
                ))}
              </ButtonGroup>
              {selectedGroup !== 'all' && (
                <Select
                  size="sm"
                  width="220px"
                  value={selectedSubgroupId}
                  onChange={(e) => setSelectedSubgroupId(e.target.value)}
                >
                  <option value="">All subgroups</option>
                  {subgroups.filter(subgroup => subgroup.group === selectedGroup).map(subgroup => (
                    <option key={subgroup.id} value={subgroup.id}>{subgroup.name} ({subgroup.patientCount})</option>
                  ))}
                </Select>
              )}
            </HStack>
          </HStack>
        </Box>

        {/* KPI Cards */}
        <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing={6}>
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <Stat>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <HStack spacing={1}>
                    <StatLabel color="gray.600" fontSize="sm">Total Patients</StatLabel>
                    <Badge size="xs" colorScheme="blue" variant="subtle">LIVE</Badge>
                  </HStack>
                  <StatNumber fontSize="2xl" color="blue.500">{kpiData.totalPatients.toLocaleString()}</StatNumber>
                </VStack>
                <Icon as={FiUsers} w={8} h={8} color="blue.500" />
              </HStack>
            </Stat>
          </Box>

          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <Stat>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <HStack spacing={1}>
                    <StatLabel color="gray.600" fontSize="sm">Active Patients</StatLabel>
                    <Badge size="xs" colorScheme="green" variant="subtle">LIVE</Badge>
                  </HStack>
                  <StatNumber fontSize="2xl" color="green.500">{kpiData.activePatients.toLocaleString()}</StatNumber>
                </VStack>
                <Icon as={FiActivity} w={8} h={8} color="green.500" />
              </HStack>
            </Stat>
          </Box>

          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <Stat>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <StatLabel color="gray.600" fontSize="sm">Critical Alerts</StatLabel>
                  <StatNumber fontSize="2xl" color="red.500">{kpiData.criticalAlerts}</StatNumber>
                </VStack>
                <Icon as={FiAlertTriangle} w={8} h={8} color="red.500" />
              </HStack>
            </Stat>
          </Box>
        </SimpleGrid>

        {/* Cohort analysis of the selected group */}
        {selectedGroup !== 'all' && (
          <CohortAnalysis
            group={selectedGroup}
            subgroupId={selectedSubgroupId ? Number(selectedSubgroupId) : null}
            refreshKey={cohortRefreshKey}
          />
        )}

        {/* Main Charts Grid */}
        <SimpleGrid columns={1} spacing={6}>
          {/* Patient Overview Chart */}
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <HStack justify="space-between" align="center" mb={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="bold" color="blue.500">
                  Patient Overview & Growth
                </Text>
                {lastUpdated && (
                  <Text fontSize="xs" color="gray.500">
                    Last updated: {lastUpdated.toLocaleString()}
                  </Text>
                )}
              </VStack>
              <Button
                size="sm"
                leftIcon={<FiRefreshCw />}
                colorScheme="blue"
                variant="ghost"
                onClick={loadPatientsData}
                isLoading={patientsLoading || loadingPersons}
              >
                Refresh
              </Button>
            </HStack>

            {patientsLoading || loadingPersons ? (
              <VStack spacing={3} align="center" py={16}>
                <Spinner size="lg" color="blue.500" />
                <Text color="gray.500" fontSize="sm">
                  Loading patient growth data...
                </Text>
              </VStack>
            ) : error ? (
              <VStack spacing={3} align="center" py={16}>
                <Text color="red.500" fontSize="sm" fontWeight="medium">
                  Failed to load growth data
                </Text>
                <Button size="sm" colorScheme="blue" variant="outline" onClick={loadPatientsData}>
                  Retry Loading
                </Button>
              </VStack>
            ) : (
              <>
                <ApexChart
                  options={patientOverviewChart.options}
                  series={patientOverviewChart.series}
                  type="area"
                  height={300}
                />
              </>
            )}
          </Box>

          {/* PublicToken Distribution */}
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <HStack justify="space-between" align="center" mb={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="bold" color="purple.500">
                  {selectedGroup === 'all' ? 'Patient Type Distribution' : `${groupLabel(selectedGroup)} Subgroup Distribution`}
                </Text>
                {lastUpdated && (
                  <Text fontSize="xs" color="gray.500">
                    Last updated: {lastUpdated.toLocaleString()}
                  </Text>
                )}
              </VStack>
              <Button
                size="sm"
                leftIcon={<FiRefreshCw />}
                colorScheme="purple"
                variant="ghost"
                onClick={loadPatientsData}
                isLoading={patientsLoading || loadingPersons}
              >
                Refresh
              </Button>
            </HStack>

            {patientsLoading || loadingPersons ? (
              <VStack spacing={3} align="center" py={16}>
                <Spinner size="lg" color="purple.500" />
                <Text color="gray.500" fontSize="sm">
                  Loading patient type data...
                </Text>
              </VStack>
            ) : error ? (
              <VStack spacing={3} align="center" py={16}>
                <Text color="red.500" fontSize="sm" fontWeight="medium">
                  Failed to load distribution data
                </Text>
                <Button size="sm" colorScheme="purple" variant="outline" onClick={loadPatientsData}>
                  Retry Loading
                </Button>
              </VStack>
            ) : (
              <>
                <ApexChart
                  options={publicTokenDistribution.options}
                  series={publicTokenDistribution.series}
                  type="donut"
                  height={300}
                />
                {/* Distribution Summary */}
                {publicTokenData.series.length > 0 && (
                  <Box mt={4} p={3} bg={statBg} borderRadius="md">
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      {publicTokenData.labels.map((label, index) => (
                        <VStack key={label} spacing={1}>
                          <Text fontSize="lg" fontWeight="bold" color={
                            selectedGroup !== 'all' ? 'purple.500' :
                              label === 'Glucose' ? 'green.500' :
                                label === 'Stress' ? 'red.500' :
                                  'blue.500'
                          }>
                            {publicTokenData.series[index] || 0}
                          </Text>
                          <Text fontSize="xs" color="gray.600" textAlign="center">
                            {selectedGroup === 'all' ? `${label} Patients` : label}
                          </Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </>
            )}
          </Box>
        </SimpleGrid>

        {/* Age Distribution Analytics; age and sex only come with the glucose data */}
        {(selectedGroup === 'all' || selectedGroup === 'VISAVITA_GLUCOSE') && (
        <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
          <HStack justify="space-between" align="center" mb={6}>
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold" color="green.500">
                Glucose Patient Demographics
              </Text>
              <Text fontSize="sm" color="gray.600">
                Age and sex distribution for glucose patients
              </Text>
              {lastUpdated && (
                <Text fontSize="xs" color="gray.500">
                  Last updated: {lastUpdated.toLocaleString()}
                </Text>
              )}
            </VStack>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              colorScheme="green"
              variant="ghost"
              onClick={loadPatientsData}
              isLoading={patientsLoading || loadingPersons || loadingDemographics}
            >
              Refresh
            </Button>
          </HStack>

          {patientsLoading || loadingPersons || loadingDemographics ? (
            <VStack spacing={3} align="center" py={16}>
              <Spinner size="lg" color="green.500" />
              <Text color="gray.500" fontSize="sm">
                {loadingDemographics ? "Loading demographics data..." : "Loading glucose patient data..."}
              </Text>
            </VStack>
          ) : error ? (
            <VStack spacing={3} align="center" py={16}>
              <Text color="red.500" fontSize="sm" fontWeight="medium">
                Failed to load glucose patient demographics
              </Text>
              <Text color="gray.500" fontSize="xs" textAlign="center" maxW="300px">
                {error}
              </Text>
              <Button size="sm" colorScheme="green" variant="outline" onClick={loadPatientsData}>
                Retry Loading
              </Button>
            </VStack>
          ) : (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Box>
                <ApexChart
                  options={createAgeDistributionChart(ageDistributionData.glucose, 'Sex Count by Age Group - Glucose Patients', 'green').options}
                  series={createAgeDistributionChart(ageDistributionData.glucose, 'Sex Count by Age Group - Glucose Patients', 'green').series}
                  type="bar"
                  height={350}
                />
              </Box>
              <Box>
                <ApexChart
                  options={createSexDistributionPieChart(ageDistributionData.glucose, 'Sex Distribution - Glucose Patients', 'green').options}
                  series={createSexDistributionPieChart(ageDistributionData.glucose, 'Sex Distribution - Glucose Patients', 'green').series}
                  type="pie"
                  height={350}
                />
              </Box>
            </SimpleGrid>
          )}
        </Box>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <HStack justify="space-between" mb={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="bold" color="teal.500">
                  Recent Patients
                </Text>
              </VStack>
              {!patientsLoading && recentPatients.length > 0 && (
                <VStack align="end" spacing={1}>
                  <AvatarGroup size="sm" max={3}>
                    {recentPatients.slice(0, 3).map((patient) => (
                      <Avatar key={patient.id} name={patient.avatar} bg="purple.500" />
                    ))}
                  </AvatarGroup>
                  <Text fontSize="xs" color="gray.500">
                    {recentPatients.length} patients loaded
                  </Text>
                </VStack>
              )}
            </HStack>

            {patientsLoading || loadingPersons ? (
              <VStack spacing={3} align="center" py={8}>
                <Spinner size="lg" color="purple.500" />
                <Text color="gray.500" fontSize="sm">
                  {loadingPersons ? "Loading available persons..." : "Loading patient data..."}
                </Text>
              </VStack>
            ) : error ? (
              <VStack spacing={3} align="center" py={8}>
                <Text color="red.500" fontSize="sm" fontWeight="medium">
                  Failed to load patients
                </Text>
                <Text color="gray.500" fontSize="xs" textAlign="center" maxW="300px">
                  {error}
                </Text>
                <VStack spacing={2}>
                  <Button size="sm" colorScheme="purple" variant="outline" onClick={loadPatientsData}>
                    Retry Loading
                  </Button>
                  <Text fontSize="xs" color="gray.400">
                    Check browser console for detailed logs
                  </Text>
                </VStack>
              </VStack>
            ) : recentPatients.length === 0 ? (
              <VStack spacing={3} align="center" py={8}>
                <Text color="gray.500" fontSize="sm">No recent patients available</Text>
                <Text color="gray.400" fontSize="xs">Patient data will appear here when available</Text>
              </VStack>
            ) : (
              <VStack spacing={3} align="stretch">
                {recentPatients.map((patient) => (
                  <Box
                    key={patient.id}
                    p={3}
                    bg={statBg}
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                    onClick={() => history.push(`/admin/patient-details/${patient.identifier}`)}
                  >
                    <HStack justify="space-between">
                      <HStack spacing={3}>
                        <Avatar name={patient.avatar} size="sm" bg="purple.500" />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">{patient.name}</Text>
                          <Text fontSize="xs" color="gray.500">ID: {patient.externalIdentifier}</Text>
                          <Text fontSize="xs" color="gray.400">Active Patient</Text>
                        </VStack>
                      </HStack>
                      <Badge colorScheme="purple" variant="subtle" px={2} py={1}>
                        {patient.publicToken}
                      </Badge>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          {/* Alerts & Notifications */}
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <HStack justify="space-between" mb={4}>
              <Text fontSize="lg" fontWeight="bold" color="red.500">
                Recent Alerts
              </Text>
              {alertsData.length > 0 && (
                <Badge colorScheme="red" variant="subtle" px={2} py={1}>
                  {alertsData.length} new
                </Badge>
              )}
            </HStack>

            {patientsLoading || loadingPersons ? (
              <VStack spacing={3} align="center" py={8}>
                <Spinner size="lg" color="red.500" />
                <Text color="gray.500" fontSize="sm">
                  {loadingPersons ? "Loading available persons..." : "Loading alerts..."}
                </Text>
              </VStack>
            ) : error || alertsData.length === 0 ? (
              <VStack spacing={3} align="center" py={8}>
                <Text color="gray.500" fontSize="sm">
                  {error ? "Failed to load alerts" : "No recent alerts"}
                </Text>
                <Text color="gray.400" fontSize="xs" textAlign="center">
                  {error ? "Patient alerts will appear here when data is available" : "All patients are stable"}
                </Text>
              </VStack>
            ) : (
              <VStack spacing={3} align="stretch">
                {alertsData.map((alert) => (
                  <Box key={alert.id} p={3} bg={statBg} borderRadius="md" borderLeft="4px" borderColor={`${getAlertColor(alert.type)}.500`}>
                    <VStack align="start" spacing={1}>
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" fontWeight="medium">{alert.patient}</Text>
                        <Badge colorScheme={getAlertColor(alert.type)} size="sm" px={2} py={1}>
                          {alert.type}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">{alert.message}</Text>
                      <Text fontSize="xs" color="gray.400">{alert.time}</Text>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>
        </SimpleGrid>
      </VStack>
    </Container>
  );
};
export default BiomDashboard;
